import {
  IDestinationEntityJobRunOptions,
  type IDestinationEntity,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  BASIC_MEDICAL_HISTORY_FORM_SCHEMA,
  Brand,
  CustomFormConfiguration,
  CustomFormContent,
  toMedicalHistoryForm,
} from '@principle-theorem/principle-core';
import {
  ISO_DATE_FORMAT,
  snapshot,
  snapshotCombineLatest,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  BasePatientMedicalHistoryDestinationEntity,
  type IPatientMedicalHistoryFormSubmission,
  type IPatientMedicalHistoryJobData,
} from '../../../destination/entities/patient-medical-histories';
import { getValidMedicalHistoryFormOptions } from '../../../mappings/allergy-to-medical-history';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  processAllergyRows,
  processMedicalHistoryRows,
} from '../../mappings/medical-history-processing';
import {
  PatientSourceEntity,
  type PraktikaPatient,
} from '../../source/entities/patient';
import { PatientMedicalAllergySourceEntity } from '../../source/entities/patient-medical-allergy';
import { PatientMedicalHistorySourceEntity } from '../../source/entities/patient-medical-history';
import { PatientDestinationEntity } from './patients';

type JobData = IPatientMedicalHistoryJobData<PraktikaPatient>;

export class PraktikaPatientMedicalHistoryDestinationEntity extends BasePatientMedicalHistoryDestinationEntity<
  PraktikaPatient,
  JobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    medicalHistories: new PatientMedicalHistorySourceEntity(),
    allergies: new PatientMedicalAllergySourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {};

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const brand$ = PracticeMigration.brand$(migration);

    return snapshotCombineLatest([brand$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [brand]]) =>
        sourcePatients.map(
          (sourcePatient): JobData => ({
            sourcePatient,
            brand,
            medicalConditions: [],
            allergies: [],
          })
        )
      )
    );
  }

  async buildFormSubmission(
    migration: WithRef<IPracticeMigration>,
    data: JobData
  ): Promise<IPatientMedicalHistoryFormSubmission | undefined> {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();

    const [historyRows, allergyRows] = await Promise.all([
      this.sourceEntities.medicalHistories.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      ),
      this.sourceEntities.allergies.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      ),
    ]);

    if (!historyRows.length && !allergyRows.length) {
      return;
    }

    const config = await snapshot(Brand.medicalHistoryFormConfig$(data.brand));
    const content = config
      ? await CustomFormConfiguration.getContent(config)
      : undefined;
    const jsonSchemaForm = content
      ? CustomFormContent.getJsonSchemaForm(content)
      : {};
    const form = toMedicalHistoryForm(jsonSchemaForm);
    const schema = form?.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;

    const formOptions = getValidMedicalHistoryFormOptions(schema);

    let formData = processMedicalHistoryRows(historyRows, formOptions);
    formData = processAllergyRows(allergyRows, formData, formOptions);

    const rawDate = data.sourcePatient.data.patient_medicalhistorydate;
    const date = rawDate
      ? toTimestamp(
          moment
            .tz(rawDate, ISO_DATE_FORMAT, migration.configuration.timezone)
            .endOf('day')
        )
      : toTimestamp();

    return {
      ...form,
      data: formData,
      date,
      disableChanges: true,
    };
  }
}
